import { ReactNode } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import { UnitBatch } from '../../api/types'
import { reconstructedAmount } from '../../calc/preview'
import { StatusChip } from '../../components/StatusChip'
import { tokens } from '../../theme'
import { formatCzk, formatDate, formatDateTime, formatUnits } from './format'

/** Malý odznak „override" (vzor ManualBadge z Platby). */
function OverrideChip() {
  return (
    <Box
      component="span"
      sx={{
        ml: 0.75,
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '9999px',
        fontWeight: 600,
        fontSize: '0.6875rem',
        px: '6px',
        py: '1px',
        bgcolor: tokens.warning[100],
        color: tokens.warning[700],
      }}
    >
      override
    </Box>
  )
}

interface BatchDetailDialogProps {
  open: boolean
  batch: UnitBatch | null
  onClose: () => void
}

/** Detail přepočtu (audit): souhrn, účastníci s kontrolou, výsledky testů a AI. */
export function BatchDetailDialog({ open, batch, onClose }: BatchDetailDialogProps) {
  // Jméno klienta pro AI výsledky (BE posílá id + denormalizované jméno).
  const clientName = (clientId: string, fallback: string) =>
    batch?.participants.find((p) => p.clientId === clientId)?.clientName ?? fallback

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {batch && (
        <>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <span>
                Přepočet {batch.id} — {batch.fundKey}
              </span>
              <StatusChip status={batch.status} />
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 0.5 }}>
              <Box
                component="dl"
                sx={{
                  m: 0,
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  columnGap: 3,
                  rowGap: 1,
                }}
              >
                <SummaryRow label="Datum přidělení" value={formatDate(batch.assignmentDate)} />
                <SummaryRow label="NAV" value={formatCzk(batch.navTotal)} />
                <SummaryRow label="Cena jednotky" value={formatUnits(batch.unitPrice)} />
                <SummaryRow
                  label="Jednotky před → po"
                  value={`${formatUnits(batch.totalUnitsBefore)} → ${formatUnits(batch.totalUnitsAfter)}`}
                />
                <SummaryRow
                  label="Vytvořil"
                  value={`${batch.createdBy} · ${formatDateTime(batch.createdAt)}`}
                />
                {batch.decidedBy && batch.decidedAt && (
                  <SummaryRow
                    label="Rozhodl"
                    value={`${batch.decidedBy} · ${formatDateTime(batch.decidedAt)}`}
                  />
                )}
              </Box>

              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Klient</TableCell>
                      <TableCell align="right">Vklad</TableCell>
                      <TableCell align="right">Cena</TableCell>
                      <TableCell align="right">Jednotky</TableCell>
                      <TableCell align="right">Kontrola (cena × jednotky)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batch.participants.map((p) => {
                      const price = p.priceOverride ?? batch.unitPrice
                      return (
                        <TableRow key={p.depositId}>
                          <TableCell>{p.clientName}</TableCell>
                          <TableCell align="right">{formatCzk(p.amount)}</TableCell>
                          <TableCell align="right">
                            {formatUnits(price)}
                            {p.priceOverride != null && <OverrideChip />}
                          </TableCell>
                          <TableCell align="right">
                            {formatUnits(p.units)}
                            {p.unitsOverride != null && <OverrideChip />}
                          </TableCell>
                          <TableCell align="right">
                            {formatCzk(reconstructedAmount(p.units, price))}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </Box>

              {batch.check && (
                <>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Automatické testy
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {batch.check.autoTests.map((test) => (
                        <Box key={test.name} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          {test.passed ? (
                            <CheckCircleOutlineIcon
                              fontSize="small"
                              sx={{ color: tokens.success[500], mt: '2px' }}
                            />
                          ) : (
                            <CancelOutlinedIcon
                              fontSize="small"
                              sx={{ color: tokens.error[500], mt: '2px' }}
                            />
                          )}
                          <Box>
                            <Typography variant="body2">{test.name}</Typography>
                            {test.detail && (
                              <Typography variant="caption" color="text.secondary" component="div">
                                {test.detail}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      AI kontrola
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {batch.check.aiResults.map((result) => (
                        <Box key={result.id}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {clientName(result.id, result.name)} —{' '}
                            <Box
                              component="span"
                              sx={{
                                color:
                                  result.status === 'OK'
                                    ? tokens.success[700]
                                    : tokens.error[700],
                              }}
                            >
                              {result.status}
                            </Box>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="div">
                            {result.comment}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="text" color="inherit" onClick={onClose}>
              Zavřít
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ pt: '2px' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </>
  )
}
